import {
  BookOutlined,
  CheckCircleFilled,
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  CloseSquareOutlined,
  EditFilled,
  ExclamationCircleOutlined,
  LoadingOutlined,
  MailOutlined,
  PlusCircleOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  SwitcherOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useOrders } from '@api/useOrders'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { OrderUpdatedTag } from '@components/OrderDispatch/OrderGrid/OrderUpdatedTag'
import {
  TankAlertType,
  WindowAlertType,
} from '@components/StoreMonitoring/StoreDashboard/TankAlertDisplay/components/AnalyticIcon'
import { OptimizingIcon } from '@components/_SiteWide/OrderDisplay/OptimizingIcon'
import { OrderProducts } from '@components/_SiteWide/OrderDisplay/OrderProducts'
import { OrderStatusTag } from '@components/_SiteWide/OrderDisplay/OrderStatusTag'
import { OrderStores } from '@components/_SiteWide/OrderDisplay/OrderStores'
import { DispatchStatusTag } from '@components/_SiteWide/StoreDisplay/DispatchStatusTag'
import {
  DataItem,
  ErrorNotification,
  GraviButton,
  Horizontal,
  NotificationMessage,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { useUserSettings } from '@hooks/settings'
import { useModelConfig } from '@hooks/useModelConfig'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { ReadableDifference, getCarrierDeliveryWindow, getTimeUntilRetain } from '@utils/general'
import { DateOrMoment, formatters } from '@utils/intl/dateTimeFormatters'
import { Button, Col, Form, Input, Popover, Row, Select, Tooltip, notification } from 'antd'
import moment from 'moment'
import { type MouseEvent, type ReactNode, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import type { OrderState } from '@/components/OrderMovements/OrderMovements/MovementDetail/BOL/Detail/constants'
import { OrderFlagIcon, OrderPriceFlagIcon } from '@/components/_SiteWide/Flags/FlagIcon'
import { OrderModalButton } from '@/components/_SiteWide/OrderDisplay/OrderModalButton'
import { OrderTerminals } from '@/components/_SiteWide/OrderDisplay/OrderTerminals'
import { formatTimeWindow, normalizeDispatchDate } from '@/components/_SiteWide/OrderDisplay/OrderTimeFormats'
import { completeOrder, getTimeSinceEmailSent, removeRows, sendToCarrier } from '@/pages/OrderManagement/util'
import { useLockedOrders } from '@hooks/useLockedOrders'
import type { RowNode } from 'ag-grid-community'
import useDispatchWindowFilter from '../Filters/DispatchWindow/deprecated_useDispatchWindowFilter'
import useLoadingWindowFilter from '../Filters/LoadingWindow'
import useRetainFilter from '../Filters/Retain/useRetainFilter'
import useRunoutFilter from '../Filters/Runout/deprecated_useRunoutFilter'
import { getOrderWindowAlertIcon } from './DefaultColumnDefs'
import type {
  ButtonsCellRendererProps,
  CarrierActionsCellRendererProps,
  CarrierRejectButtonProps,
  CompleteButtonProps,
  DestinationsCellRendererProps,
  GridOrder,
  OrderFlagsCellRendererProps,
  OrderRejectFormProps,
  POCellRendererProps,
  PriceFlagRendererProps,
  ReassignButtonProps,
  ResendEmailButtonProps,
  SendButtonProps,
  SentTimeCellRendererProps,
  StageButtonProps,
  UpdatedTagCellRendererProps,
} from './types'
import { shouldHideNoFitRunout } from './util'
import dayjs from 'dayjs'

const { Option } = Select
const { TextArea } = Input

export function ButtonsCellRenderer({ value, showModal, data }: ButtonsCellRendererProps) {
  const { scopes } = useSecurityContext()
  const canViewBOL = scopes?.order_movements_page
  const isComplete = data.state === 'complete'
  const { updateSetting } = useUserSettings()
  const { lockedOrders } = useLockedOrders()
  const lockedOrder = useMemo(() => lockedOrders?.find((o) => o.order_id === data.id), [lockedOrders, data?.id])
  const isDisabled = data?.state === 'canceled'
  return (
    <Horizontal style={{ overflow: 'visible' }} gap={8} verticalCenter>
      <Vertical style={{ overflow: 'visible' }} verticalCenter flex={1}>
        <OrderModalButton orderId={value} showModal={showModal} isDisabled={isDisabled} />
      </Vertical>
      <Vertical style={{ overflow: 'visible' }} verticalCenter flex={1}>
        <Tooltip title='View BOL' mouseEnterDelay={0.5} placement='bottom'>
          {canViewBOL && (
            <Link
              to={`/order_movements_page/report?${new URLSearchParams({ order_number: data.number?.toString() || '' })}`}
              target='_blank'
              onClick={() => {
                updateSetting('current-order-movements-report-view', 'all')
                localStorage.setItem('current-order-movements-report-view', 'all')
              }}
            >
              <GraviButton size='small' icon={<BookOutlined />} />
            </Link>
          )}
        </Tooltip>
      </Vertical>
      <Vertical flex={1}>{lockedOrder && <OptimizingIcon lockedOrder={lockedOrder} />}</Vertical>
    </Horizontal>
  )
}

export const contractCellRenderer = ({ value }: { value: string[] }) => (
  <Texto className='text-truncate'>{value?.map((v, index) => (index === 0 ? v : `, ${v}`))}</Texto>
)

export function OrderFlagsCellRenderer({ data, api }: OrderFlagsCellRendererProps) {
  if (data.state === 'canceled') return null
  const queryClient = useQueryClient()

  const refresh = () => {
    queryClient
      .invalidateQueries({
        queryKey: ['getOrdersQuery'],
      })
      .then(() => {
        api?.refreshCells({ force: true })
      })
  }

  return <OrderFlagIcon row={data} updateOrder={refresh} />
}

export function PriceFlagRenderer({ data, handleOrderChange }: PriceFlagRendererProps) {
  return <OrderPriceFlagIcon row={data} updateOrder={handleOrderChange} />
}

export function OrderStateCellRenderer(row: { value: { state: OrderState } }) {
  return <OrderStatusTag status={row.value.state} />
}

export function DestinationsCellRenderer({ setOrderHistoryStoreId, data }: DestinationsCellRendererProps) {
  return <OrderStores order={data} setOrderHistoryStoreId={setOrderHistoryStoreId} />
}
export function OriginsCellRenderer({ data }: RowNode) {
  return <OrderTerminals order={data} showShortName={false} />
}
export function ProductsCellRenderer({ node, value }: { node: RowNode; value: string[] }) {
  if (node.group) return value
  return <OrderProducts order={node.data} showAlerts />
}

export const LoadingWindowCellRenderer = ({ data }: RowNode) => {
  return formatTimeWindow(
    data.load_window_start,
    data.load_window_end,
    data.type?.toLowerCase() !== 'backhaul',
    false,
    data.load_window_start_local
  )
}

function CarrierWindowRender(order: GridOrder) {
  // Renderer for carrier orders

  if (!order.carrier_window_start || !order.carrier_window_end) {
    return <Texto appearance='medium'>Carrier Window Undefined</Texto>
  }
  if (moment(order.carrier_window_start) >= moment(order.carrier_window_end)) {
    return <Texto appearance='medium'>Impossible Carrier Window</Texto>
  }

  const uniqueWindowAlerts = Array.from(new Map(order.window_alert?.map((alert) => [alert.type, alert])).values())
    .filter((alert) => Object.values(WindowAlertType).includes(alert.type as WindowAlertType))
    .map((alert) => getOrderWindowAlertIcon(alert.type as WindowAlertType, alert.threshold))
  const isComplete = order?.state === 'complete'
  const deliveryWindow = getCarrierDeliveryWindow(order)

  const content = (
    <Row align='middle' style={{ minWidth: '350px' }}>
      {uniqueWindowAlerts}
      <Texto
        className={`dispatch-window-tag ${deliveryWindow?.includes('Now') ? 'in-window' : ''}`}
        weight={!isComplete ? 'bold' : 'normal'}
      >
        {deliveryWindow}
      </Texto>
    </Row>
  )

  return !isComplete ? (
    <Popover
      placement='top'
      title={<Texto category='label'>Carrier Window Details</Texto>}
      content={
        <CarrierWindowPopoverContent
          order={order}
          carrierStart={moment(order?.carrier_window_start)}
          carrierEnd={moment(order?.carrier_window_end)}
        />
      }
    >
      {content}
    </Popover>
  ) : (
    content
  )
}

function DispatchWindowRender(order: Gravitate.Order) {
  // Render for driver / non carrier orders
  const uniqueWindowAlerts = Array.from(new Map(order.window_alert?.map((alert) => [alert.type, alert])).values())
    .filter((alert) => Object.values(WindowAlertType).includes(alert.type as WindowAlertType))
    .map((alert) => getOrderWindowAlertIcon(alert.type as WindowAlertType, alert.threshold))
  const isComplete = order?.state === 'complete'
  const deliveryWindow = formatTimeWindow(
    order.dispatch_window_start,
    order.dispatch_window_end,
    order.type?.toLowerCase() !== 'backhaul',
    false,
    order.dispatch_window_start_local
  )
  return (
    <Horizontal style={{ minWidth: '350px' }}>
      {uniqueWindowAlerts}
      <Texto
        className={`dispatch-window-tag ${deliveryWindow?.includes('Now') ? 'in-window' : ''}`}
        weight={!isComplete ? 'bold' : 'normal'}
      >
        {deliveryWindow}
      </Texto>
    </Horizontal>
  )
}

export function DeliveryWindowCellRenderer({ data }: RowNode) {
  // Standard Delivery Window Render for Carrier & Driver Orders
  if (data.carrier || data.manager) {
    return CarrierWindowRender(data)
  }
  return DispatchWindowRender(data)
}

export function RunoutCellRenderer({ data }: RowNode<GridOrder>) {
  const end = data?.tank_dispatch_window_end ?? data?.dispatch_window_end
  const runoutAlertTypes: TankAlertType[] = [
    TankAlertType.Empty,
    TankAlertType.RunoutWithinWarning,
    TankAlertType.RunoutWithinError,
  ]

  const runoutAlert = data?.window_alert?.find((alert: { type: TankAlertType }) =>
    runoutAlertTypes.includes(alert.type)
  )
  const runoutIcon = runoutAlert ? getOrderWindowAlertIcon(runoutAlert.type, runoutAlert.threshold) : undefined

  if (shouldHideNoFitRunout(data)) return <Texto appearance='medium'>N/A</Texto>
  return (
    <Horizontal>
      {runoutIcon}
      <Col>{ReadableDifference(end, undefined, true, data?.type?.toLowerCase() === 'backhaul')}</Col>
    </Horizontal>
  )
}

export function NoFitCellRenderer({ data }: RowNode) {
  const start = data?.tank_dispatch_window_start ?? data?.dispatch_window_start
  const noFitAlertTypes: (typeof WindowAlertType)[] = [WindowAlertType.NoFit, WindowAlertType.PotentialNoFit]

  const noFitAlert = data.window_alert?.find((alert: { type: typeof WindowAlertType }) =>
    noFitAlertTypes.includes(alert.type)
  )
  const noFitIcon = noFitAlert ? getOrderWindowAlertIcon(noFitAlert.type, noFitAlert.threshold) : undefined

  if (shouldHideNoFitRunout(data)) return <Texto appearance='medium'>N/A</Texto>
  return (
    <Horizontal>
      {noFitIcon}
      <Col>{getTimeUntilRetain(moment(start))}</Col>
    </Horizontal>
  )
}

export function POCellRenderer({ data, isPopoverVisible, setIsPopoverVisible, canEdit }: POCellRendererProps) {
  const [isLoading, setIsLoading] = useState(false)
  const visible = useMemo(() => isPopoverVisible === data.id, [isPopoverVisible, data.id])
  const [po, setPo] = useState(data?.po)
  const { updatePOMutation } = useOrders()

  if (canEdit && data.state !== 'canceled' && data?.type !== 'Order Request (Requested)') {
    const handleSave = async () => {
      setIsLoading(true)
      try {
        await updatePOMutation.mutateAsync({ order_id: data.id, po: po || null })
      } finally {
        setIsPopoverVisible('')
        setIsLoading(false)
      }
    }
    const handleCancel = () => {
      setIsPopoverVisible('')
      setPo(data?.po || '')
    }
    const content = (
      <>
        <Horizontal className='mb-4'>
          <Input
            placeholder='Add PO or Reference Number'
            style={{ width: '100%' }}
            value={po ?? ''}
            onChange={(e) => setPo(e.target.value)}
            maxLength={30}
          />
        </Horizontal>
        <hr className='light-gray-hr' style={{ width: '110%' }} />
        <Horizontal className='p-2' justifyContent='space-between'>
          <GraviButton buttonText='Cancel' onClick={handleCancel} disabled={isLoading} />
          <GraviButton
            buttonText={isLoading ? <LoadingOutlined /> : 'Save'}
            success
            onClick={handleSave}
            disabled={isLoading}
          />
        </Horizontal>
      </>
    )

    return (
      <Popover
        visible={visible}
        content={content}
        title={<Texto category='label'>PO Number</Texto>}
        destroyTooltipOnHide
      >
        <GraviButton
          disabled={isLoading}
          onClick={() => setIsPopoverVisible(data?.id)}
          className='ghost-gravi-button'
          buttonText={
            data?.po ? (
              <Texto weight='400' align='left'>
                {data.po}
              </Texto>
            ) : (
              <Texto appearance='secondary'>
                <PlusCircleOutlined className='mr-2' />
                Add PO Number
              </Texto>
            )
          }
        />
      </Popover>
    )
  }
  return <Texto align='left'>{data.po}</Texto>
}

export function OrderNoCellRenderer(number: { value: number }) {
  return <Texto appearance='primary'>{number.value}</Texto>
}

export function UpdatedTagCellRenderer({ data, setAuditOrderId }: UpdatedTagCellRendererProps) {
  return <OrderUpdatedTag order={data} setAuditOrderId={setAuditOrderId} />
}

export function CarrierActionsCellRenderer({
  data,
  triggerTPDModal,
  scopes,
  orderStatus,
  api,
}: CarrierActionsCellRendererProps) {
  const {
    sendCarrierOrderMutation,
    completeOrderMutation,
    uncompleteOrderMutation,
    stageCarrierOrderMutation,
    unstageCarrierOrderMutation,
  } = useOrders()
  const { mutateAsync: sendOrder } = sendCarrierOrderMutation
  const { mutateAsync: completeOrderMutate } = completeOrderMutation
  const { lockedOrders } = useLockedOrders()
  const isCarrier = scopes?.is_carrier
  const canEditOrders = useMemo(
    () => !lockedOrders?.map((order) => order.order_id).includes(data.id) && scopes.scopes.includes('o.w'),
    [lockedOrders, data?.id, scopes?.scopes]
  )
  const isSent = useMemo(() => data.carrier_notify_state === 'sent', [data.carrier_notify_state])
  const isPending = useMemo(() => data.carrier_notify_state === 'pending', [data.carrier_notify_state])
  const isStaged = useMemo(() => data.carrier_notify_state === 'staged', [data.carrier_notify_state])
  const isCrossroadsOrder = !!data.carrier_info?.client_goid
  const isDeliveredSupply = data.is_delivered
  const isDisabled = useMemo(() => {
    const complianceHold = data.flags?.find((flag) => flag.type === 'compliance_hold')
    const creditHold = data.flags?.find((flag) => flag.type === 'credit_hold')
    return Boolean(
      complianceHold && creditHold
        ? 'Compliance Hold & Credit Hold'
        : complianceHold
          ? 'Compliance Hold'
          : creditHold
            ? 'Credit Hold'
            : false
    )
  }, [])
  if (isCarrier || !canEditOrders || data?.state === 'canceled') return null

  const handleReassign = () => triggerTPDModal(data.id, data.carrier)
  const handleResend = () => sendToCarrier(data, orderStatus, true, sendOrder, api, false)
  const handleComplete = () => completeOrder(data, orderStatus, completeOrderMutate, api)
  const handleSend = (sendWithoutEmail: boolean) =>
    sendToCarrier(data, orderStatus, false, sendOrder, api, sendWithoutEmail)
  const handleStage = async () => {
    try {
      if (!isStaged) {
        await stageCarrierOrderMutation.mutateAsync([data.id])
        notification.open({ message: 'Carrier Order Staged', description: `Order ${data?.number} has been staged.` })
        api.refreshCells({ force: true })
      } else {
        await unstageCarrierOrderMutation.mutateAsync(data.id)
        notification.open({
          message: 'Carrier Order Unstaged',
          description: `Order ${data?.number} has been unstaged.`,
        })
        api.refreshCells({ force: true })
      }
    } catch (e) {
      NotificationMessage(
        'Carrier Order Update Failed',
        'Please try again or contact your system administrator if this continues.'
      )
    }
  }

  if (data.state === 'complete') {
    if (isCrossroadsOrder) return null
    if (!data.can_uncomplete) return null
    return (
      <Horizontal justifyContent='start' style={{ overflow: 'visible' }}>
        <Tooltip title='Uncomplete Order'>
          <GraviButton
            appearance='outlined'
            error
            icon={<CloseOutlined />}
            onClick={async () => {
              try {
                await uncompleteOrderMutation.mutateAsync({ order_id: data.id, orderStatus }).then(() => {
                  api.refreshCells({ force: true })
                })
              } catch (e) {
                NotificationMessage(
                  'Could not uncomplete order.',
                  'Please try again or contact your system administrator if this continues.'
                )
              }
            }}
          />
        </Tooltip>
      </Horizontal>
    )
  }
  if (isCrossroadsOrder) {
    if (!data.carrier_info?.crossroads_status) {
      return (
        <Horizontal>
          {!isDeliveredSupply ? (
            <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />
          ) : (
            <Vertical flex={1} />
          )}
          <StageButton isDisabled={isDisabled} handleStage={handleStage} isStaged={isStaged} />
          <SendButton isDisabled={isDisabled} handleSend={handleSend} />
        </Horizontal>
      )
    }
    if (data.carrier_info?.crossroads_status === 'accepted') {
      return (
        <Horizontal style={{ overflow: 'visible' }}>
          {!isDeliveredSupply ? (
            <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />
          ) : (
            <Vertical flex={1} />
          )}
          <StageButton isDisabled={isDisabled} handleStage={handleStage} isStaged={isStaged} />
          <ResendEmailButton handleResend={handleResend} isPending={isPending} />
        </Horizontal>
      )
    }

    if (data.carrier_info?.crossroads_status === 'assigned') {
      return null
    }

    if (data.carrier_info?.crossroads_status === 'rejected' || data.carrier_info?.crossroads_status === 'error') {
      return (
        <Horizontal style={{ overflow: 'visible' }}>
          {!isDeliveredSupply ? (
            <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />
          ) : (
            <Vertical flex={1} />
          )}
          <StageButton isDisabled={isDisabled} handleStage={handleStage} isStaged={isStaged} />
          <SendButton isDisabled={isDisabled} handleSend={handleSend} />
        </Horizontal>
      )
    }
  } else {
    if (isPending) {
      return (
        <Horizontal justifyContent='start' style={{ overflow: 'visible' }}>
          {!isDeliveredSupply && <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />}
        </Horizontal>
      )
    }
    if (isStaged) {
      return (
        <Horizontal style={{ overflow: 'visible' }}>
          {!isDeliveredSupply ? (
            <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />
          ) : (
            <Vertical flex={1} />
          )}
          <StageButton isDisabled={isDisabled} handleStage={handleStage} isStaged={isStaged} />
          <SendButton isDisabled={isDisabled} handleSend={handleSend} />
        </Horizontal>
      )
    }
    if (isSent) {
      return (
        <Horizontal style={{ overflow: 'visible' }}>
          {!isDeliveredSupply ? (
            <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />
          ) : (
            <Vertical flex={1} />
          )}
          <ResendEmailButton handleResend={handleResend} isPending={isPending} />
          <CompleteButton isPending={isPending} handleComplete={handleComplete} />
        </Horizontal>
      )
    }
    if (!data.carrier && !data.manager) return null
    return (
      <Horizontal style={{ overflow: 'visible' }}>
        {!isDeliveredSupply ? (
          <ReassignButton isDisabled={isDisabled} handleReassign={handleReassign} />
        ) : (
          <Vertical flex={1} />
        )}
        <StageButton isDisabled={isDisabled} handleStage={handleStage} />
        <SendButton isDisabled={isDisabled} handleSend={handleSend} />
      </Horizontal>
    )
  }
}

function ReassignButton({ handleReassign, isDisabled }: ReassignButtonProps) {
  return (
    <Vertical flex={1}>
      <Tooltip title={isDisabled || 'Reassign'}>
        {isDisabled ? (
          <Button style={{ background: 'transparent', padding: 0, border: 'none' }} onClick={() => null}>
            <GraviButton
              icon={<UploadOutlined />}
              onClick={handleReassign}
              style={{ pointerEvents: 'auto' }}
              disabled={isDisabled}
            />
          </Button>
        ) : (
          <GraviButton icon={<UploadOutlined />} onClick={handleReassign} />
        )}
      </Tooltip>
    </Vertical>
  )
}

function ResendEmailButton({ handleResend, isPending }: ResendEmailButtonProps) {
  return (
    <Vertical flex={1}>
      <Tooltip title='Resend Email'>
        <GraviButton title='Resend Email' icon={<MailOutlined />} disabled={isPending} onClick={handleResend} />
      </Tooltip>
    </Vertical>
  )
}

function CompleteButton({ handleComplete, isPending }: CompleteButtonProps) {
  return (
    <Vertical flex={1}>
      <Tooltip title='Complete'>
        <GraviButton color='green' icon={<CheckOutlined />} disabled={isPending} onClick={handleComplete} />
      </Tooltip>
    </Vertical>
  )
}

function SendButton({ isDisabled, handleSend }: SendButtonProps) {
  const { config } = useModelConfig()
  const [loading, setLoading] = useState(false)

  const handleSendClick = async (sendWithoutEmail: boolean) => {
    setLoading(true)
    await handleSend(sendWithoutEmail)
    setLoading(false)
  }

  if (config.can_mark_as_sent_no_email) {
    return (
      <Vertical flex={1}>
        <Tooltip title={isDisabled || 'Send'}>
          <Popover
            placement='bottomRight'
            trigger='click'
            content={
              <Vertical style={{ gap: '0.5rem', minWidth: 180 }}>
                <GraviButton
                  buttonText='Mark As Sent, No Email'
                  loading={loading}
                  onClick={() => handleSendClick(true)}
                />
                <GraviButton
                  buttonText='Send To Carrier'
                  theme2
                  loading={loading}
                  onClick={() => handleSendClick(false)}
                />
              </Vertical>
            }
          >
            <div>
              {isDisabled ? (
                <Button style={{ background: 'transparent', padding: 0, border: 'none' }} onClick={() => null}>
                  <GraviButton disabled={isDisabled} icon={<SendOutlined />} loading={loading} theme2 />
                </Button>
              ) : (
                <Button style={{ background: 'transparent', padding: 0, border: 'none' }} onClick={() => null}>
                  <GraviButton icon={<SendOutlined />} loading={loading} theme2 />
                </Button>
              )}
            </div>
          </Popover>
        </Tooltip>
      </Vertical>
    )
  }

  return (
    <Vertical flex={1}>
      <Tooltip title={isDisabled || 'Send'}>
        {isDisabled ? (
          <Button style={{ background: 'transparent', padding: 0, border: 'none' }} onClick={() => null}>
            <GraviButton
              disabled={isDisabled}
              icon={<SendOutlined />}
              loading={loading}
              theme2
              appearance='outline'
              onClick={() => handleSendClick(false)}
            />
          </Button>
        ) : (
          <GraviButton
            icon={<SendOutlined />}
            loading={loading}
            theme2
            appearance='outline'
            onClick={() => handleSendClick(false)}
          />
        )}
      </Tooltip>
    </Vertical>
  )
}

function StageButton({ handleStage, isDisabled, isStaged }: StageButtonProps) {
  return (
    <Vertical flex={1}>
      <Tooltip title={isStaged ? 'Unstage' : 'Stage'}>
        <GraviButton
          className='outline'
          disabled={Boolean(isDisabled)}
          icon={isStaged ? <SwitcherOutlined /> : <SafetyCertificateOutlined />}
          onClick={handleStage}
        />
      </Tooltip>
    </Vertical>
  )
}

function CarrierRejectButton({ isCarrierUser, data, gridAPIRef }: CarrierRejectButtonProps) {
  if (!isCarrierUser) return null
  const [loading, setLoading] = useState(false)
  const [popoverVisible, setPopoverVisible] = useState(false)
  const [form] = Form.useForm()

  const handleRejectOrder = async () => {
    setLoading(true)
    const request = {
      order_id: data.id,
      reason_code: form.getFieldValue('reason_code'),
      note: form.getFieldValue('note'),
    }
    if (data.state === 'assigned' && !data.driver) {
      await api.crossroadsCarrierReject(request)
      handleNotify()
    } else {
      handleNotify(true)
    }
    setPopoverVisible(false)
    setLoading(false)
  }
  const handleNotify = (isError = false) => {
    const rowToUpdate = gridAPIRef && gridAPIRef.current?.getRowNode(data.id)

    if (!isError) {
      NotificationMessage(`Order ${data.number} successfully rejected`, 'Owner notified', false)
      removeRows([rowToUpdate], gridAPIRef)
    } else {
      NotificationMessage('Unable to reject order', `Order is already ${data.state}, Please refresh page.`)
    }
    setPopoverVisible(false)
    setLoading(false)
  }

  return (
    <div>
      {data.state === 'assigned' && data.carrier_info?.type !== 'legacy' && (
        <Popover
          title={<Texto category='label'>{`Reject Order #${data.number}`}</Texto>}
          content={
            <OrderRejectForm
              setPopoverVisible={setPopoverVisible}
              handleRejectOrder={handleRejectOrder}
              form={form}
              setLoading={setLoading}
              loading={loading}
            />
          }
          onConfirm={(e: MouseEvent) => {
            e.preventDefault()
            handleRejectOrder()
          }}
          trigger='click'
          visible={popoverVisible}
          placement='rightTop'
        >
          <GraviButton
            icon={<CloseOutlined />}
            error
            disabled={loading}
            loading={loading}
            onClick={() => setPopoverVisible(!popoverVisible)}
          />
        </Popover>
      )}
    </div>
  )
}

function OrderRejectForm({ setPopoverVisible, handleRejectOrder, form, setLoading, loading }: OrderRejectFormProps) {
  const reasonCodes = ['No Driver Capacity', 'No Carded Drivers', 'No Drivers in Market', 'Load Distance too Long']
  return (
    <div style={{ width: '270px' }}>
      <Texto appearance='medium' className='pb-3'>
        Use this action if you are not able to run a load.
      </Texto>

      <Form onFinish={handleRejectOrder} onFinishFailed={ErrorNotification} form={form}>
        <Horizontal justifyContent='spece-between' verticalCenter>
          <Vertical>
            <Texto category='label' className='pb-4'>
              Reason
            </Texto>
          </Vertical>
          <Vertical>
            <Form.Item
              name='reason_code'
              className='mt-1'
              rules={[{ required: true, message: 'Reason Code is required' }]}
            >
              <Select style={{ width: '200px' }}>
                {reasonCodes.map((reason) => (
                  <Option value={reason} key={reason}>
                    {reason}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Vertical>
        </Horizontal>
        <Horizontal>
          <Form.Item name='note'>
            <TextArea rows={4} maxLength={350} style={{ width: '255px' }} placeholder='Enter Reject Reason Note' />
          </Form.Item>
        </Horizontal>
        <Horizontal>
          <Vertical flex={1}>
            <GraviButton
              buttonText='Cancel'
              onClick={() => {
                setLoading(false)
                setPopoverVisible(false)
              }}
              style={{ width: '70%' }}
            />
          </Vertical>
          <Vertical flex={1}>
            <GraviButton
              buttonText='Reject Order'
              error
              htmlType='submit'
              style={{ width: '75%' }}
              className='ml-4'
              disabled={loading}
              loading={loading}
            />
          </Vertical>
        </Horizontal>
      </Form>
    </div>
  )
}

export function SentTimeCellRenderer({ data, setChangeId }: SentTimeCellRendererProps) {
  const [visible, setVisible] = useState(false)
  const handleVisibleChange = (newVisible: boolean) => {
    setVisible(newVisible)
  }
  function RejectionNoteContents() {
    return (
      <div className='p-3'>
        <Horizontal>
          <Vertical style={{ maxWidth: 66 }}>
            <Texto appearance='medium' weight='bold'>
              Reason:
            </Texto>
          </Vertical>
          <Vertical justifyContent='flex-start'>
            <Texto align='left' weight='bold'>
              {data.carrier_rejected_reason}
            </Texto>
          </Vertical>
        </Horizontal>
        <Horizontal className='pt-3'>
          <Vertical style={{ maxWidth: 66 }}>
            <Texto appearance='medium' weight='bold'>
              Note:
            </Texto>
          </Vertical>
          <Vertical justifyContent='flex-start'>
            <Texto align='left'>{data.carrier_rejected_note}</Texto>
          </Vertical>
        </Horizontal>
      </div>
    )
  }

  const getStatus = () => {
    function TextWithIcon({ Icon, children }: { Icon: ReactNode; children: ReactNode }) {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {Icon}
          <p style={{ margin: 0 }}>{children}</p>
        </div>
      )
    }
    // 1. x-roads order errors or requested change error
    // 2. requested changes (status = requested or rejected )
    // 3. x-roads order status (normal)
    // 4. x-roads notify states
    const update = data.carrier_info?.crossroads_queued_update
    if (update && update.status !== 'accepted') {
      return {
        appearance: ['rejected', 'error'].includes(update.status) ? 'error' : 'purple',
        display: (() => {
          switch (update.status) {
            case 'requested':
              return (
                <TextWithIcon Icon={<ClockCircleOutlined />}>
                  Changes Requested{' '}
                  <a style={{ fontWeight: 'normal' }} onClick={() => setChangeId(update.id)}>
                    (View)
                  </a>
                </TextWithIcon>
              )
            case 'rejected':
              return <TextWithIcon Icon={<CloseSquareOutlined />}>Changes Rejected</TextWithIcon>
            case 'error':
              return <TextWithIcon Icon={<ExclamationCircleOutlined />}>Request Error </TextWithIcon>
            default:
              return () => <></>
          }
        })(),
      }
    }
    if (data.state === 'complete') {
      return {
        appearance: 'success',
        display: (
          <>
            <CheckCircleFilled /> Completed
          </>
        ),
      }
    }
    if (data.state === 'canceled') {
      return { appearance: 'medium', display: 'Canceled' }
    }

    if (data.carrier_info?.crossroads_status === 'error') {
      return { appearance: 'error', display: 'Error' }
    }
    if (data.carrier_info?.crossroads_status === 'accepted') {
      return { appearance: 'purple', display: 'Accepted' }
    }
    if (data.carrier_info?.crossroads_status === 'assigned') {
      return { appearance: 'purple', display: 'Driver Assigned' }
    }
    if (data.carrier_info?.crossroads_status === 'rejected') {
      return {
        appearance: 'error',
        display: (
          <>
            <CloseOutlined />
            Rejected &nbsp;
            <Popover
              content={RejectionNoteContents}
              title={`Order #${data.number} Rejected`}
              trigger='click'
              open={visible}
              onOpenChange={handleVisibleChange}
              overlayInnerStyle={{ maxWidth: 350 }}
            >
              <a style={{ fontWeight: 'normal' }}>(View Note)</a>
            </Popover>
          </>
        ),
      }
    }
    if (data.carrier_notify_state === 'failed') {
      return { appearance: 'error', display: 'Email Failed' }
    }
    if (data.carrier_notify_state === 'sent') {
      return { appearance: 'secondary', display: 'Email Sent' }
    }
    if (data.carrier_notify_state === 'pending') {
      return { appearance: 'medium', display: 'Pending' }
    }
    if (data.carrier_notify_state === 'staged') {
      return { appearance: 'medium', display: 'Staged' }
    }
    return { appearance: 'medium', display: 'Assigned' }
  }
  const status = getStatus()

  return (
    <div className='vertical-flex-center'>
      <div className='mb-1'>
        {status.appearance === 'purple' ? (
          <Texto style={{ color: 'var(--theme-color-4)' }} textTransform='capitalize' category='p1' weight='bold'>
            {status.display}
          </Texto>
        ) : (
          <div>
            <Texto appearance={status.appearance} style={{ textTransform: 'capitalize' }} weight='bold'>
              {status.display}
            </Texto>
          </div>
        )}
      </div>
      {status.display !== 'Canceled' && (
        <div className='flex'>
          <Texto category='label' weight='bold' textTransform='uppercase'>
            {formatters.SHORT_DATE_TIME(data.carrier_updated, data.carrier_updated_local)}
          </Texto>
          <Texto className='pl-2' appearance='medium' weight={600} category='label'>
            ({getTimeSinceEmailSent(data)} ago)
          </Texto>
        </div>
      )}
    </div>
  )
}

export function DispatchStatusCellRenderer({ data, showTime }) {
  return (
    <div className='vertical-flex-center'>
      <DispatchStatusTag stackCarrierWithDriver order={data} showTime={showTime} />
    </div>
  )
}

export function CarrierStatusCellRenderer({ data }) {
  return (
    <div className='vertical-flex-center'>
      <Texto className='text-ellipsis pr-2' weight='bold'>
        {data.carrier || data.manager}
      </Texto>
    </div>
  )
}
export function CarrierNameCellRenderer({ data }) {
  return <b>{data?.carrier}</b>
}
export function CarrierReceivedCellRenderer({ data }) {
  return <b>{data.received_at ? formatters.DATE_TIME(moment(data.received_at)) : ''}</b>
}

export const SiteETACellRenderer = ({ data }) => {
  return getDateRows(data.eta, data.eta_d2)
}
const getDateRows = (first, second) => {
  if (!first && !second) return null

  if (first && second)
    return (
      <div>
        <Row>
          <Col>{formatters.MONTH_DATE_TIME(moment(first))}</Col>
        </Row>
        <Row>
          <Col>{formatters.MONTH_DATE_TIME(moment(second))}</Col>
        </Row>
      </div>
    )

  return <div>{formatters.MONTH_DATE_TIME(moment(first))}</div>
}
export function CarrierDelWinCellRenderer({ data }) {
  // TODO We should deprecate this, ive moved the order dispatch grids to both use DispatchWindowFilter
  // This is still in use on the order drawer on site monitoring overview
  const carrierStart = moment(data.carrier_window_start)
  const carrierEnd = moment(data.carrier_window_end)
  const retainTime = moment(data.dispatch_window_start)
  const runoutTime = moment(data.dispatch_window_end)

  if (!carrierStart.isValid() || !carrierEnd.isValid()) {
    return <Texto>Impossible Carrier Window</Texto>
  }

  const isBeforeRetain = carrierStart.isBefore(retainTime)
  const isAfterRunout = carrierEnd.isAfter(runoutTime)

  const getTextAppearance = (windowType) => {
    if (windowType === 'start') {
      if (isBeforeRetain && isAfterRunout) {
        return 'error'
      }
      if (isBeforeRetain) {
        return 'warning'
      }
    }
    if (windowType === 'end') {
      if (isBeforeRetain && isAfterRunout) {
        return 'error'
      }
      if (isAfterRunout) {
        return 'warning'
      }
    }
  }
  const isComplete = data.state === 'complete'
  return (
    <>
      {isComplete ? (
        <Texto appearance={isComplete ? 'default' : 'secondary'} className='mr-5 carrier-window-cell'>
          {formatTimeWindow(
            data.carrier_window_start,
            data.carrier_window_end,
            false,
            false,
            data.carrier_window_start_local
          )}
        </Texto>
      ) : (
        <Popover
          placement='top'
          title={<Texto category='label'>Carrier Window Details</Texto>}
          content={
            <CarrierWindowPopoverContent
              order={data}
              isBeforeRetain={isBeforeRetain}
              isAfterRunout={isAfterRunout}
              getTextAppearance={getTextAppearance}
              carrierStart={carrierStart}
              carrierEnd={carrierEnd}
            />
          }
        >
          <Button className='carrier-window-cell p-0 mr-5' type='text'>
            <div className='flex'>
              <Texto appearance={getTextAppearance('start')}>
                {normalizeDispatchDate(carrierStart)} &nbsp; - &nbsp;
              </Texto>
              <Texto appearance={getTextAppearance('end')}>{normalizeDispatchDate(carrierEnd)}</Texto>
            </div>
          </Button>
        </Popover>
      )}
    </>
  )
}

function CarrierWindowPopoverContent({
  order,
  carrierStart,
  carrierEnd,
}: {
  order: GridOrder
  carrierStart: DateOrMoment
  carrierEnd: DateOrMoment
}) {
  const retain = order?.tank_dispatch_window_start ?? order.dispatch_window_start
  const runout = order?.tank_dispatch_window_end ?? order.dispatch_window_end
  return (
    <Row style={{ minWidth: '275px' }}>
      <Col span={12}>
        <DataItem label='Retain Time'>
          {formatters.DATE_TIME(dayjs(retain), order.dispatch_window_start_local)}
        </DataItem>
        <DataItem label='Carrier Start'>
          <Texto>{formatters.DATE_TIME(carrierStart, order.carrier_window_start_local)}</Texto>
        </DataItem>
      </Col>
      <Col align='right'>
        <DataItem label='Runout Time'>{formatters.DATE_TIME(dayjs(runout), order.dispatch_window_end_local)}</DataItem>
        <DataItem label='Carrier End'>
          <Texto>{formatters.DATE_TIME(carrierEnd, order.carrier_window_end_local)}</Texto>
        </DataItem>
      </Col>
    </Row>
  )
}

function CarrierUpdateViewCell({ data, setChangeId }) {
  const xroadsStatus = data.carrier_info?.crossroads_status
  const update = data.carrier_info?.crossroads_queued_update
  function TextWithIcon({ Icon, children }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {Icon}
        <p style={{ margin: 0 }}>{children}</p>
      </div>
    )
  }

  if (update && update.status === 'accepted') {
    return <TextWithIcon Icon={<CheckOutlined />}>Up To Date</TextWithIcon>
  }

  const getStatus = () => {
    // 1. x-roads order errors or requested change error
    // 2. requested changes (status = requested or rejected )
    // 3. x-roads order status (normal)
    // 4. x-roads notify states

    if (['rejected', 'error'].includes(xroadsStatus))
      return {
        appearance: 'error',
        display: xroadsStatus,
      }

    if (update && update.status !== 'accepted') {
      return {
        appearance: ['rejected', 'error'].includes(update.status) ? 'error' : 'warning',
        display: (() => {
          switch (update.status) {
            case 'requested':
              return (
                <TextWithIcon Icon={<EditFilled />}>
                  Changes Requested{' '}
                  <a style={{ fontWeight: 'normal' }} onClick={() => setChangeId(update.id)}>
                    (View)
                  </a>
                </TextWithIcon>
              )
            case 'rejected':
              return <TextWithIcon Icon={<CloseSquareOutlined />}>Changes Rejected</TextWithIcon>
            case 'error':
              return <TextWithIcon Icon={<ExclamationCircleOutlined />}>Request Error </TextWithIcon>
            default:
              return () => <>Up To Date</>
          }
        })(),
      }
    }
    if (data.state === 'complete') {
      return {
        appearance: 'success',
        display: (
          <>
            <CheckCircleFilled /> Completed
          </>
        ),
      }
    }
    return 'Up To Date'
  }

  const status = getStatus()

  return (
    <div className='vertical-flex-center'>
      <div className='mb-1'>
        {status.appearance === 'purple' ? (
          <Texto style={{ color: 'var(--theme-color-4)' }} textTransform='capitalize' category='p1' weight='bold'>
            {status.display}
          </Texto>
        ) : (
          <div>
            <Texto appearance={status.appearance} style={{ textTransform: 'capitalize' }} weight='bold'>
              {status.display}
            </Texto>
          </div>
        )}
      </div>
      <div className='flex'>
        <Texto category='label' weight='bold'>
          {formatters.SHORT_DATE_TIME(moment(data.carrier_updated))}
        </Texto>
        <Texto className='pl-2' appearance='medium' weight={600} category='label'>
          ({getTimeSinceEmailSent(data)} ago)
        </Texto>
      </div>
    </div>
  )
}

function FlagUpdatedRenderer({ data }: RowNode) {
  const priceFlagDate = data?.price_flag?.updated
  if (priceFlagDate) {
    return (
      <Texto category='label' weight='bold'>
        {formatters.SHORT_DATE_TIME(moment(priceFlagDate))}
      </Texto>
    )
  }
  return null
}

export const orderViewColumns = {
  buttonsCellRenderer: ButtonsCellRenderer,
  orderFlagsCellRenderer: OrderFlagsCellRenderer,
  priceFlagRenderer: PriceFlagRenderer,
  flagUpdatedRenderer: FlagUpdatedRenderer,
  orderStateCellRenderer: OrderStateCellRenderer,
  carrierStatusCellRenderer: CarrierStatusCellRenderer,
  dispatchStatusCellRenderer: DispatchStatusCellRenderer,
  hasUpdatesCellRenderer: CarrierUpdateViewCell,
  destinationsCellRenderer: DestinationsCellRenderer,
  originsCellRenderer: OriginsCellRenderer,
  productsCellRenderer: ProductsCellRenderer,
  loadingWindowCellRenderer: LoadingWindowCellRenderer,
  dispatchWindowCellRenderer: DeliveryWindowCellRenderer,
  carrierDelWinCellRenderer: CarrierDelWinCellRenderer,
  runoutCellRenderer: RunoutCellRenderer,
  poCellRenderer: POCellRenderer,
  orderNoCellRenderer: OrderNoCellRenderer,
  updatedTagCellRenderer: UpdatedTagCellRenderer,
  carrierActionsCellRenderer: CarrierActionsCellRenderer,
  carrierRecievedCellRender: CarrierReceivedCellRenderer,
  sentTimeCellRenderer: SentTimeCellRenderer,
  useDispatchWindowFilter,
  useLoadingWindowFilter,
  useRunoutFilter,
  useRetainFilter,
  carrierRejectButton: CarrierRejectButton,
}
